import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import { rangeInputStyles } from 'src/library/editors/components/range-input-styles';
import 'src/library/elements/d-popover';
import 'src/library/elements/d-loading-image';
import 'src/library/elements/d-loading-video';
import 'src/content/screens/d-media-gallery';
import type { LibraryItem } from 'src/library/screen/library-item';
import type { DPopover } from 'src/library/elements/d-popover';
import { resolveAssetUrl } from './resolve-asset-url';
import { t } from 'src/i18n';

/**
 *
 */
@customElement('d-slide-media')
export class DSlideMedia extends LitElement {
  static readonly styles = [
    rangeInputStyles,
    css`
      :host {
        display: flex;
        position: relative;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        overflow: hidden;
        /* container-type: size lar slide-level placeholder skalere SVG-meldingen
           proporsjonalt med slide-rammen og hide label på små thumbnails. */
        container-type: size;
        /* Prevent unwanted selection #12812 */
        user-select: none;
      }
      :host([edit]) {
        cursor: grab;
      }
      #media-wrapper {
        position: absolute;
        background: black;
      }
      #media-wrapper.moving {
        cursor: grabbing;
      }
      #media {
        display: block;
        width: auto;
        height: 100%;
      }
      d-loading-image#media,
      d-loading-video#media {
        position: relative;
      }
      d-loading-image#media::part(img),
      d-loading-video#media::part(video) {
        display: block;
        width: auto;
        height: 100%;
        object-fit: cover;
      }
      img,
      video {
        object-fit: cover;
        position: relative;
      }
      .overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
      }
      /* Slide-level placeholder vises mens media lastes / konverteres. Ligger
         som sibling av #media-wrapper (utenfor zoom/pan-laget) så meldingen er
         alltid sentrert i slide uavhengig av wrapper-størrelse og -aspekt.
         cqmin gjør at SVG-ikon og label skaleres proporsjonalt med slide. */
      .placeholder {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        background: #1f2937;
        color: #e5e7eb;
        font-family: -apple-system, system-ui, sans-serif;
        line-height: 1.2;
        text-align: center;
        gap: 2cqmin;
        padding: 4cqmin;
        font-size: 4cqmin;
        pointer-events: none;
      }
      .placeholder svg.video-icon,
      .placeholder svg.missing-icon {
        width: 14cqmin;
        height: 14cqmin;
        flex: none;
      }
      /* Skjul tekst i små visninger — gråfelt + ikon er nok kontekst.
         Tekst skaleres ikke meningsfullt under ~140px og blir bare en
         uleselig flekk. */
      @container (max-width: 140px) or (max-height: 140px) {
        .placeholder > .label {
          display: none;
        }
      }
      .controls-positioner {
        position: absolute;
        width: 50px;
        height: 50px;
        border-radius: 22px;
        transition: opacity 0.3s;
      }
      @media (hover: hover) {
        :host([hide-controls]) .controls-positioner {
          opacity: 0;
        }
      }
      .controls-positioner.left {
        top: 0;
        left: 0;
        transform: rotate(90deg);
        transform-origin: 25px 25px;
      }
      .controls-positioner.right {
        top: 0;
        right: 0;
        transform: rotate(90deg);
      }
      .controls-positioner.top {
        top: 0;
        left: 0;
      }
      .controls-positioner.bottom {
        bottom: 0;
        left: 0;
      }
      .controls-positioner * {
        flex: none;
      }
      .controls {
        position: absolute;
        display: flex;
        gap: 6px;
        align-items: center;
        height: 50px;
        width: 300px;
        max-width: calc(100cqw - 24px);
        pointer-events: none;
        transform: scale(1);
        transform-origin: top left;
        transition: all 0.3s;
      }
      .left .controls,
      .bottom .controls {
        transform-origin: bottom left;
      }
      .controls * {
        pointer-events: all;
      }
      .controls-positioner.left .controls,
      .controls-positioner.right .controls {
        /* Vertikal slider: bruk container-høyden direkte i stedet for å
           anta 16:9-aspekt. Det gjør slider-lengden uavhengig av drag i
           horisontal retning (left/right layout). */
        max-width: calc(100cqh - 24px);
      }
      .select-image {
        flex: none;
        width: 50px;
        height: 50px;
        background: url(/images/select-image.svg) 50% 50% no-repeat;
        background-size: 56px;
        cursor: pointer;
      }
      .controls-positioner.left .select-image,
      .controls-positioner.right .select-image {
        margin-right: -8px;
        transform: rotate(-90deg);
      }
      .controls-positioner.left .select-image,
      .controls-positioner.right .select-image {
        transform: rotate(-90deg);
      }
      .size-input {
        flex: 1;
        width: 200px;
      }
      .controls-positioner.left .size-input,
      .controls-positioner.right .size-input {
        transform: rotate(180deg);
      }
      @media only screen and (max-width: 600px) {
        .controls {
          transform: scale(0.7);
        }
        .size-input {
          flex: 1;
          width: 300px;
        }
      }
    `,
  ];
  @property({ type: Boolean, reflect: true })
  edit = false;
  @property({ type: String, attribute: 'layout-position', reflect: true })
  layoutPosition: 'full' | 'top' | 'right' | 'bottom' | 'left' = 'full';
  @property({ type: String })
  src = '';
  @property({ type: Number })
  left = 10;
  @property({ type: Number })
  top = 0;
  @property({ type: Number })
  scale = 100;
  @property({ type: String })
  overlayColor = '#000';
  @property({ type: Number })
  overlayOpacity = 0;
  @property({ type: Number })
  blurring = 0;
  @property({ type: Array })
  media: string[] = [];
  @property({ type: Array })
  library: LibraryItem[] = [];
  @property({ type: String })
  assetBase = '';
  @query('#media-wrapper')
  mediaWrapper!: HTMLDivElement;
  @query('d-popover')
  popoverElm!: DPopover;
  @state()
  containerWidth = 0;
  @state()
  containerHeight = 0;
  @state()
  clientStartLeft = 0;
  @state()
  clientStartTop = 0;
  @state()
  mediaWrapperStartLeft = 0;
  @state()
  mediaWrapperStartTop = 0;
  @state()
  isMediaLandscape = false;
  @state()
  private naturalAspect = 0;
  @state()
  private naturalImageHeight = 0;
  @state()
  private initialFitNeeded = false;
  /** True når <d-loading-image>/<d-loading-video> har dispatchet sin loaded-event.
      Styrer slide-level placeholder, zoom-slider og pan-handlers. */
  @state()
  private mediaLoaded = false;

  /** True når <d-loading-image>/<d-loading-video> rapporterer at media ikke kunne
      lastes (typisk 404 fra en brutt referanse). Styrer «Bilde mangler»-melding
      i slide-level placeholder i stedet for en taus blank flate. */
  @state()
  private mediaError = false;

  @state()
  private videoDuration = 10;

  private resizeObserver?: ResizeObserver;
  private prevContainerWidth = 0;
  private prevContainerHeight = 0;

  /** Resolved URL for display. `src` itself holds the canonical reference. */
  private get displaySrc(): string {
    return resolveAssetUrl(this.src, this.assetBase);
  }

  /**
   * Library-thumbnailen (base64 data-URL) for gjeldende media, hvis den finnes.
   * Brukes som blur-up-placeholder mens fullbildet lastes/dekodes.
   */
  private get thumbnailSrc(): string {
    if (!this.src) return '';
    const item = this.library.find((i) => i.assets?.includes(this.src));
    return item?.thumbnail ?? '';
  }

  private get isImage(): boolean {
    const imageExtensions = ['.gif', '.jpg', '.jpeg', '.png', '.webp', '.avif', '.svg'];
    const ref = this.src.split('?')[0];
    const extension = ref.slice(ref.lastIndexOf('.')).toLowerCase();
    return imageExtensions.includes(extension);
  }

  private get isVideo(): boolean {
    // Speiler PreviewSignageProvider.SIGNAGE_MEDIA_TYPES på backend, samt
    // extensionForMime i d-media-gallery — liste-mismatch betyr at en upload
    // som backend lagrer (f.eks. .webm, .mkv, .m4v, .3gp) ikke renderes.
    const videoExtensions = ['.mpg', '.mp2', '.mpeg', '.mp4', '.m4v', '.avi', '.mov', '.wmv', '.webm', '.mkv', '.3gp'];
    const ref = this.src.split('?')[0];
    const extension = ref.slice(ref.lastIndexOf('.')).toLowerCase();
    return videoExtensions.includes(extension);
  }

  private get mediaWrapperStyle() {
    return {
      left: this.left + '%',
      top: this.top + '%',
      width: this.scale + '%',
      height: this.scale + '%',
      filter: 'blur(' + this.blurring + 'cqw)',
    };
  }

  private get imageControlsAlignment() {
    let left = false;
    let right = false;
    let top = false;
    let bottom = false;
    if (this.layoutPosition === 'left') {
      left = true;
    }
    if (this.layoutPosition === 'right') {
      right = true;
    }
    if (this.layoutPosition === 'top') {
      top = true;
    }
    if (this.layoutPosition === 'bottom') {
      bottom = true;
    }
    return { left, right, top, bottom };
  }

  connectedCallback() {
    super.connectedCallback();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.onContainerResize());
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.prevContainerWidth = 0;
    this.prevContainerHeight = 0;
  }

  /**
   * Når rammen endrer størrelse: behold bildets pikselstørrelse og piksel-
   * posisjon relativt den *faste* kanten av rammen (drevet av layoutPosition).
   * Clamp deretter til cover-garantien — det gir uncrop / drag / zoom som
   * tre implicitte faser uten egen logikk.
   *
   *   - layoutPosition='top'    → topp er fast (i ekstern verden); imageTopPx
   *                               i element-lokale koordinater forblir lik.
   *   - layoutPosition='bottom' → bunn er fast; bildets avstand fra ramme-
   *                               bunnen forblir lik.
   *   - 'left'/'right'          → tilsvarende horisontalt.
   *   - 'full'/udefinert        → ingen drag-resize i praksis; behandles
   *                               som 'top'/'left' (ingen reposisjonering).
   *
   * Clamp-reglene (cover-garanti):
   *   imageTopPx <= 0  (bildet topp på eller over ramme-topp)
   *   imageTopPx + imageHpx >= newH  (bildet bunn på eller under ramme-bunn)
   * Hvis begge ikke kan oppfylles fordi bildet er for lite, bumpas imageHpx
   * (zoom) til minimum cover-størrelse.
   */
  private onContainerResize() {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    // Kun redigering skal kunne re-skalere/-posisjonere eller sende events. En
    // visning/thumbnail (edit=false) viser lagrede, prosent-baserte verdier som
    // de er — rammen er alltid 16:9, så uniform resize trenger ingen omregning.
    // Uten denne vakten forstørrer en preview/thumbnail bildet (og lekker
    // endringen tilbake til den faktiske sliden) ved hover/animasjon. Se #12907.
    if (!this.edit) {
      this.prevContainerWidth = rect.width;
      this.prevContainerHeight = rect.height;
      return;
    }

    let changed = false;
    if (this.prevContainerWidth && this.prevContainerHeight && this.naturalAspect > 0) {
      const oldH = this.prevContainerHeight;
      const oldW = this.prevContainerWidth;
      const newH = rect.height;
      const newW = rect.width;

      // Hvis ramme-aspektet er uendret (typisk vindus-resize: slide forblir
      // 16:9, bare totalstørrelsen endres), er prosent-baserte scale/top/left
      // allerede gyldige etter resize — alt skal skaleres uniformt. Hopp over
      // piksel-bevarings-logikken, ellers blir bildet zoomet/forskjøvet kun
      // pga. vinduendring (issue #12842).
      const oldAspect = oldW / oldH;
      const newAspect = newW / newH;
      const aspectChanged = Math.abs(newAspect - oldAspect) > 1e-3;
      if (!aspectChanged) {
        this.prevContainerWidth = newW;
        this.prevContainerHeight = newH;
        if (this.applyConstraints()) this.fireValuesChanged();
        return;
      }

      let imageHpx = (this.scale / 100) * oldH;
      let imageWpx = this.naturalAspect * imageHpx;
      let imageTopPx = (this.top / 100) * oldH;
      let imageLeftPx = (this.left / 100) * oldW;

      // Behold pikselposisjon relativ den faste kanten.
      if (this.layoutPosition === 'bottom') {
        const oldDistFromBottom = oldH - (imageTopPx + imageHpx);
        imageTopPx = newH - oldDistFromBottom - imageHpx;
      }
      if (this.layoutPosition === 'right') {
        const oldDistFromRight = oldW - (imageLeftPx + imageWpx);
        imageLeftPx = newW - oldDistFromRight - imageWpx;
      }

      // Bumpa scale opp hvis bildet ikke lenger kan dekke rammen.
      const minImageHpx = Math.max(newH, newW / this.naturalAspect);
      if (imageHpx < minImageHpx) {
        imageHpx = minImageHpx;
        imageWpx = this.naturalAspect * imageHpx;
      }

      // Clamp posisjon for å sikre cover.
      if (imageTopPx > 0) imageTopPx = 0;
      if (imageTopPx + imageHpx < newH) imageTopPx = newH - imageHpx;
      if (imageLeftPx > 0) imageLeftPx = 0;
      if (imageLeftPx + imageWpx < newW) imageLeftPx = newW - imageWpx;

      const newScale = (imageHpx / newH) * 100;
      const newTop = (imageTopPx / newH) * 100;
      const newLeft = (imageLeftPx / newW) * 100;
      const eps = 1e-9;
      if (Math.abs(newScale - this.scale) > eps) {
        this.scale = newScale;
        changed = true;
      }
      if (Math.abs(newTop - this.top) > eps) {
        this.top = newTop;
        changed = true;
      }
      if (Math.abs(newLeft - this.left) > eps) {
        this.left = newLeft;
        changed = true;
      }
    }

    const dimsChanged = this.prevContainerWidth !== rect.width || this.prevContainerHeight !== rect.height;
    this.prevContainerWidth = rect.width;
    this.prevContainerHeight = rect.height;

    if (this.applyConstraints() || changed) {
      this.fireValuesChanged();
    }
    // Slider-getterne leser rect direkte; trigger re-render hvis frame
    // endret seg uten at scale/top/left ble oppdatert (kun ramme-bredde-
    // endring ved vertikal layout f.eks.). Lit batcher dette med ev. andre
    // property-changes så ekstrarendere koster lite.
    if (dimsChanged) this.requestUpdate();
  }

  /**
   * Sett left/top slik at samme bilde-fokuspunkt forblir under sentrum av
   * rammen når enten rammen eller scale endres. Brukes både ved resize av
   * rammen og ved zoom (slik at zoom alltid skjer fra midten).
   */
  private reanchorOnCenter(
    oldRect: { width: number; height: number },
    oldScale: number,
    newRect: { width: number; height: number },
    newScale: number,
  ) {
    if (this.naturalAspect <= 0) return;
    if (!oldRect.width || !oldRect.height || !newRect.width || !newRect.height) return;

    const oldImageHeightPx = (oldScale / 100) * oldRect.height;
    const oldImageWidthPx = this.naturalAspect * oldImageHeightPx;
    if (oldImageWidthPx <= 0 || oldImageHeightPx <= 0) return;

    const oldImageLeftPx = (this.left / 100) * oldRect.width;
    const oldImageTopPx = (this.top / 100) * oldRect.height;
    const focusX = (oldRect.width / 2 - oldImageLeftPx) / oldImageWidthPx;
    const focusY = (oldRect.height / 2 - oldImageTopPx) / oldImageHeightPx;

    const newImageHeightPx = (newScale / 100) * newRect.height;
    const newImageWidthPx = this.naturalAspect * newImageHeightPx;
    this.left = ((newRect.width / 2 - focusX * newImageWidthPx) / newRect.width) * 100;
    this.top = ((newRect.height / 2 - focusY * newImageHeightPx) / newRect.height) * 100;
  }

  /** Minimum scale for at bildet skal dekke rammen helt (både bredde og høyde). */
  private getMinScale(rect: { width: number; height: number }): number {
    if (!rect.width || !rect.height || this.naturalAspect <= 0) return 100;
    const containerAspect = rect.width / rect.height;
    return Math.max(100, (100 * containerAspect) / this.naturalAspect);
  }

  /**
   * Sett scale til minimum (slik at bildet såvidt dekker rammen, ingen marg)
   * og sentrer bildet horisontalt og vertikalt.
   */
  private fitImageCentered(): boolean {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height || this.naturalAspect <= 0) return false;
    const containerAspect = rect.width / rect.height;
    this.scale = this.getMinScale(rect);
    const imageWidthPercent = (this.naturalAspect / containerAspect) * this.scale;
    this.left = (100 - imageWidthPercent) / 2;
    this.top = (100 - this.scale) / 2;
    return true;
  }

  /** Returnerer true hvis scale/left/top ble justert. */
  private applyConstraints(): boolean {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height || this.naturalAspect <= 0) return false;

    const containerAspect = rect.width / rect.height;
    const minScale = this.getMinScale(rect);

    let scale = this.scale;
    if (scale < minScale) scale = minScale;

    const imageHeightRatio = scale / 100;
    const imageWidthRatio = (this.naturalAspect / containerAspect) * (scale / 100);

    const minLeft = 100 * (1 - imageWidthRatio);
    const minTop = 100 * (1 - imageHeightRatio);

    let left = this.left;
    let top = this.top;
    if (left > 0) left = 0;
    if (left < minLeft) left = minLeft;
    if (top > 0) top = 0;
    if (top < minTop) top = minTop;

    let changed = false;
    if (scale !== this.scale) {
      this.scale = scale;
      changed = true;
    }
    if (left !== this.left) {
      this.left = left;
      changed = true;
    }
    if (top !== this.top) {
      this.top = top;
      changed = true;
    }
    return changed;
  }

  private onImageLoaded(e: CustomEvent<{ naturalWidth: number; naturalHeight: number; duration?: number }>) {
    const { naturalWidth, naturalHeight, duration } = e.detail;
    if (naturalWidth > 0 && naturalHeight > 0) {
      this.naturalAspect = naturalWidth / naturalHeight;
      this.naturalImageHeight = naturalHeight;
      this.videoDuration = duration ?? 10;
      this.applyInitialFitOrConstraints();
      this.mediaLoaded = true;
      this.mediaError = false;
    }
  }

  private onMediaError() {
    this.mediaError = true;
  }

  /**
   * Etter naturalAspect er kjent: hvis brukeren nettopp valgte et nytt bilde
   * fittes det til rammen og sentreres. Ellers bare clampes mot grensene.
   */
  private applyInitialFitOrConstraints() {
    // Visning/thumbnail (edit=false) skal rendre de lagrede prosent-verdiene
    // (left/top/scale) UENDRET. Rammen er logisk alltid 16:9, men i tidslinjen
    // (d-slideshow) skaleres thumbnailen ned med en vertikal scaleY(...)-
    // transform. Da rapporterer getBoundingClientRect en sammenklemt, ikke-16:9
    // rect, og applyConstraints() ville regnet ut en helt annen minScale og
    // dermed zoomet/forskjøvet utsnittet — bildet får et annet utsnitt enn i
    // selve sliden. Vi hopper derfor over all rect-basert omregning utenfor
    // redigering. Se #13086 (og #12907 for samme prinsipp på resize/emit).
    if (!this.edit) return;

    // Auto-fit (sentrering + min-cover) er en redigerings-affordance som kjører
    // når brukeren velger nytt bilde.
    if (this.initialFitNeeded) {
      this.initialFitNeeded = false;
      if (this.fitImageCentered()) {
        this.fireValuesChanged();
        return;
      }
    }
    if (this.applyConstraints()) {
      this.fireValuesChanged();
    }
  }

  /**
   * Zoom-slideren er log-skala 25%–800% av bildets naturlige høyde, med
   * 100% (naturlig pikselstørrelse) i nedre tredjedel av sliderens
   * bevegelsesområde og rom for å zoome 8x inn. Bytter mellom slider-
   * verdi-rommet (heltall 0–1000) og natural-zoom% via eksponensiell
   * mapping.
   *
   * Naturlig størrelse som referansepunkt gjør slider-verdien uavhengig
   * av viewport: så lenge bildets pikselstørrelse er bevart ved resize,
   * forblir slider-thumb stående i ro. Slider-min settes dynamisk til
   * den natural-zoom som kreves for cover-fit i nåværende ramme — det
   * gjør at brukeren ikke kan dra slideren under det som er mulig, og
   * indikatoren viser faktisk verdi (browseren clamper drag mot min).
   */
  private static readonly ZOOM_NAT_MIN = 25;
  private static readonly ZOOM_NAT_MAX = 800;
  private static readonly SLIDER_RES = 1000;

  private static naturalToSlider(natPct: number): number {
    const lo = Math.log(DSlideMedia.ZOOM_NAT_MIN);
    const hi = Math.log(DSlideMedia.ZOOM_NAT_MAX);
    const z = Math.max(DSlideMedia.ZOOM_NAT_MIN, Math.min(DSlideMedia.ZOOM_NAT_MAX, natPct));
    return ((Math.log(z) - lo) / (hi - lo)) * DSlideMedia.SLIDER_RES;
  }

  private static sliderToNatural(s: number): number {
    const lo = Math.log(DSlideMedia.ZOOM_NAT_MIN);
    const hi = Math.log(DSlideMedia.ZOOM_NAT_MAX);
    return Math.exp(lo + (s / DSlideMedia.SLIDER_RES) * (hi - lo));
  }

  /**
   * Beregn både nåværende og minste natural-zoom uten layout-reflow:
   * prevContainerWidth/Height oppdateres øverst i onContainerResize før
   * requestUpdate() kalles, så de er garantert ferske når render kjører.
   */
  private get sliderNaturalScales(): { current: number; coverMin: number } {
    if (!this.naturalImageHeight || this.naturalAspect <= 0 || !this.prevContainerWidth || !this.prevContainerHeight) {
      return { current: 100, coverMin: DSlideMedia.ZOOM_NAT_MIN };
    }
    const imageHpx = (this.scale / 100) * this.prevContainerHeight;
    const minImageHpx = Math.max(this.prevContainerHeight, this.prevContainerWidth / this.naturalAspect);
    return {
      current: (imageHpx / this.naturalImageHeight) * 100,
      coverMin: (minImageHpx / this.naturalImageHeight) * 100,
    };
  }

  private scaleChanged(e: Event) {
    if (!this.naturalImageHeight) return;
    const rect = this.getBoundingClientRect();
    if (!rect.height) return;
    const target = e.target as HTMLInputElement;
    // Slider-verdi → naturlig zoom% → bildets pikselhøyde → frame-relativ scale%.
    const targetNat = DSlideMedia.sliderToNatural(Number(target.value));
    const newImageHpx = (targetNat / 100) * this.naturalImageHeight;
    let newScale = (newImageHpx / rect.height) * 100;
    newScale = Math.max(newScale, this.getMinScale(rect));
    this.reanchorOnCenter(rect, this.scale, rect, newScale);
    this.scale = newScale;
    this.applyConstraints();
    this.fireValuesChanged();
  }

  private onMoveStart(clientX: number, clientY: number) {
    this.containerWidth = this.getBoundingClientRect().width;
    this.clientStartLeft = clientX;
    this.mediaWrapperStartLeft = this.mediaWrapper.getBoundingClientRect().left - this.getBoundingClientRect().left;
    this.containerHeight = this.getBoundingClientRect().height;
    this.clientStartTop = clientY;
    this.mediaWrapperStartTop = this.mediaWrapper.getBoundingClientRect().top - this.getBoundingClientRect().top;
  }

  private onMove(clientX: number, clientY: number) {
    const distanceX = clientX - this.clientStartLeft;
    const newLeftPixels = this.mediaWrapperStartLeft + distanceX;
    this.left = (newLeftPixels / this.containerWidth) * 100;
    const distanceY = clientY - this.clientStartTop;
    const newTopPixels = this.mediaWrapperStartTop + distanceY;
    this.top = (newTopPixels / this.containerHeight) * 100;
    this.applyConstraints();
  }

  private onMouseMove(e: MouseEvent) {
    this.onMove(e.clientX, e.clientY);
  }

  private onMouseStop() {
    this.mediaWrapper.classList.remove('moving');
    this.removeEventListener('mousemove', this.onMouseMove);
    this.removeEventListener('mouseup', this.onMouseStop);
    this.removeEventListener('mouseout', this.onMouseStop);
    this.fireValuesChanged();
  }

  private onMouseDown(e: MouseEvent) {
    if (this.edit && this.mediaLoaded) {
      this.mediaWrapper.classList.add('moving');
      this.onMoveStart(e.clientX, e.clientY);
      this.addEventListener('mousemove', this.onMouseMove);
      this.addEventListener('mouseup', this.onMouseStop);
      this.addEventListener('mouseout', this.onMouseStop);
    }
  }

  private preventPageScroll(e: TouchEvent) {
    e.preventDefault();
  }

  private onTouchMove(e: TouchEvent) {
    this.onMove(e.touches[0].clientX, e.touches[0].clientY);
  }

  private onTouchStop() {
    document.removeEventListener('touchmove', this.preventPageScroll);
    this.removeEventListener('touchmove', this.onTouchMove);
    this.removeEventListener('touchend', this.onTouchStop);
    this.removeEventListener('touchCancel', this.onTouchStop);
    this.fireValuesChanged();
  }

  private onTouchStart(e: TouchEvent) {
    if (this.edit && this.mediaLoaded) {
      this.onMoveStart(e.touches[0].clientX, e.touches[0].clientY);
      document.addEventListener('touchmove', this.preventPageScroll, { passive: false });
      this.addEventListener('touchmove', this.onTouchMove);
      this.addEventListener('touchend', this.onTouchStop);
      this.addEventListener('touchCancel', this.onTouchStop);
    }
  }

  private onImageSelected(e: CustomEvent<{ src: string }>) {
    this.popoverElm.close();
    this.src = e.detail.src;
    this.naturalAspect = 0;
    this.mediaLoaded = false;
    this.mediaError = false;
    // Plasseres riktig (fit + sentrert) når naturalAspect er kjent etter load.
    this.initialFitNeeded = true;
    this.left = 0;
    this.top = 0;
    this.scale = 100;
    this.fireValuesChanged();
  }

  protected updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('src') && changedProps.get('src') !== undefined) {
      this.naturalAspect = 0;
      this.mediaLoaded = false;
      this.mediaError = false;
    }
  }

  private fireValuesChanged() {
    // En ikke-redigerende instans (visning/thumbnail) skal aldri mutere eller
    // sende slide-endringer videre — ellers lekker f.eks. en preview sin
    // cover-skalering tilbake til den faktiske sliden. Se #12907.
    if (!this.edit) return;
    this.dispatchEvent(
      new CustomEvent<{ src: string; left: number; top: number; scale: number; videoDuration: number }>(
        'values-changed',
        {
          bubbles: true,
          composed: true,
          detail: {
            src: this.src,
            left: this.left,
            top: this.top,
            scale: this.scale,
            videoDuration: this.videoDuration,
          },
        },
      ),
    );
  }

  /** SVG-melding for placeholder. Brutt-bilde-ikon når media ikke kunne lastes,
      ellers play-trekant-i-sirkel for video og nothing for image (kun grå
      bakgrunn mens det lastes). */
  private renderPlaceholderIcon() {
    if (this.mediaError) {
      return html`
        <svg class="missing-icon" viewBox="0 0 44 44" fill="none" stroke="#9ca3af" stroke-width="2" aria-hidden="true">
          <rect x="6" y="6" width="32" height="32" rx="3" />
          <circle cx="16" cy="16" r="3" />
          <path d="M38 28l-9-9-20 20" />
          <path d="M6 6l32 32" />
        </svg>
      `;
    }
    if (!this.isVideo) return nothing;
    return html`
      <svg class="video-icon" viewBox="0 0 44 44" aria-hidden="true">
        <circle cx="22" cy="22" r="20" fill="none" stroke="#9ca3af" stroke-width="2" />
        <path d="M17 13 L33 22 L17 31 Z" fill="#9ca3af" />
      </svg>
    `;
  }

  render() {
    if (this.isImage || this.isVideo) {
      return html`
        <div
          id="media-wrapper"
          style=${styleMap(this.mediaWrapperStyle)}
          @mousedown=${(e: MouseEvent) => this.onMouseDown(e)}
          @touchstart=${(e: TouchEvent) => this.onTouchStart(e)}
        >
          ${this.isImage
            ? html`
                <d-loading-image
                  id="media"
                  src="${this.displaySrc}"
                  asset-path="${this.src}"
                  placeholder="${this.thumbnailSrc}"
                  @image-loaded=${(e: CustomEvent<{ naturalWidth: number; naturalHeight: number }>) =>
                    this.onImageLoaded(e)}
                  @image-error=${() => this.onMediaError()}
                ></d-loading-image>
              `
            : html`
                <d-loading-video
                  id="media"
                  src="${this.displaySrc}"
                  asset-path="${this.src}"
                  autoplay
                  @video-loaded=${(e: CustomEvent<{ naturalWidth: number; naturalHeight: number; duration: number }>) =>
                    this.onImageLoaded(e)}
                ></d-loading-video>
              `}
        </div>
        ${!this.mediaLoaded && (this.mediaError || !this.isImage || !this.thumbnailSrc)
          ? // Når bildet har en library-thumbnail viser <d-loading-image> den
            // som blur-up mens fullbildet lastes — da skal ikke den ugjennom-
            // siktige placeholderen ligge over og skjule den. Behold placeholder
            // for video, bilder uten thumbnail, og feiltilfellet.
            html`
              <div class="placeholder" aria-hidden="true">
                ${this.renderPlaceholderIcon()}
                ${this.mediaError
                  ? html`<div class="label">${t('screens.mediaMissing')}</div>`
                  : this.isVideo
                    ? html`<div class="label">${t('screens.videoUploading')}</div>`
                    : nothing}
              </div>
            `
          : nothing}
        <div class="overlay color" style=${styleMap({ background: this.overlayColor })}></div>
        <div class="overlay text">
          <slot></slot>
        </div>
        ${this.edit
          ? html`
              <div class="controls-positioner ${classMap(this.imageControlsAlignment)}">
                <div class="controls">
                  <d-popover
                    manual
                    .horizontal=${this.imageControlsAlignment.right ? 'left' : 'right'}
                    .vertical=${this.imageControlsAlignment.bottom ? 'up' : 'down'}
                    vertical-adjust="12"
                  >
                    <div slot="trigger" class="select-image"></div>
                    <d-media-gallery
                      slot="content"
                      .library=${this.library}
                      .assetBase=${this.assetBase}
                      @select-media=${(e: CustomEvent<{ src: string }>) => this.onImageSelected(e)}
                      @close=${(e: CustomEvent) => {
                        e.stopPropagation();
                        this.popoverElm.close();
                      }}
                    ></d-media-gallery>
                  </d-popover>
                  ${this.mediaLoaded
                    ? (() => {
                        const { current, coverMin } = this.sliderNaturalScales;
                        return html`
                          <input
                            class="size-input"
                            type="range"
                            step="1"
                            .min=${String(
                              Math.min(DSlideMedia.SLIDER_RES, Math.ceil(DSlideMedia.naturalToSlider(coverMin))),
                            )}
                            .max=${String(DSlideMedia.SLIDER_RES)}
                            .value=${String(Math.round(DSlideMedia.naturalToSlider(current)))}
                            @input=${(e) => this.scaleChanged(e)}
                          />
                        `;
                      })()
                    : nothing}
                </div>
              </div>
            `
          : nothing}
      `;
    }
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-slide-media': DSlideMedia;
  }
}
